import React, { useEffect, useState } from 'react';
import './PaceInput.css';
import Tooltip from './Tooltip';
import { GPX_PRESETS } from '../constants/gpxPresets';

const paceInputStyle = {
    border: '1px solid #ddd',
    padding: '20px',
    margin: '20px auto',
    borderRadius: '5px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    backgroundColor: '#fffcf5',

};

const paceToSeconds = (paceStr) => {
    const match = paceStr?.match(/^(\d{1,2}):([0-5]\d)$/);
    return match ? (parseInt(match[1]) * 60 + parseInt(match[2])) : null;
};

const secondsToPace = (totalSec) => {
    const m = Math.floor(totalSec / 60);
    const s = totalSec % 60;
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
};

const EFFORT_LEVELS = [
    { key: 'S', label: 'S', diff: -30 },   // Very Hard
    { key: 'A', label: 'A', diff: -15 },   // Hard
    { key: 'B', label: 'B', diff: 0 },     // Normal
    { key: 'C', label: 'C', diff: +20 },   // Easy
    { key: 'D', label: 'D', diff: +45 },   // Very Easy
];

function PaceInput({
    startTime, finishTime, onStartTimeChange, onFinishTimeChange,
    onSimulate, itraIndex, onItraIndexChange,
    initialPace, onInitialPaceChange, gpxFileName
}) {
    const [effort, setEffort] = useState('B'); // ★ 初期値は「基準ペースのまま」
    const [showBasePace, setShowBasePace] = useState(false); // ★ 折りたたみ状態

    useEffect(() => {
        if (!gpxFileName) return;
        const preset = GPX_PRESETS.find(p => p.id === gpxFileName);

        if (preset) {
            if (preset.startTime) onStartTimeChange({ target: { value: preset.startTime } });
            if (preset.finishTime) onFinishTimeChange({ target: { value: preset.finishTime } });
        }
        const saved = localStorage.getItem(`simSettings_${gpxFileName}`);
        if (saved) {
            try {
                const data = JSON.parse(saved);
                if (data.startTime) onStartTimeChange({ target: { value: data.startTime } });
                if (data.finishTime) onFinishTimeChange({ target: { value: data.finishTime } });
                if (data.itraIndex) onItraIndexChange({ target: { value: data.itraIndex.toString() } });
                if (data.initialPace) onInitialPaceChange({ target: { value: data.initialPace } });
                if (data.effort) setEffort(data.effort);
            } catch (e) { console.warn('LocalStorage復元失敗', e); }
        }
    }, [gpxFileName]);

    const finalPace = (() => {
        const baseSec = paceToSeconds(initialPace);
        if (!baseSec) return null;
        const level = EFFORT_LEVELS.find(l => l.key === effort);
        const adjusted = baseSec + level.diff;
        return secondsToPace(Math.max(120, Math.min(900, adjusted)));
    })();

    const isValidPace = finalPace !== null;

    const handleAdjustItra = (amount) => {
        const newValue = Math.min(1000, Math.max(1, Number(itraIndex) + amount));
        onItraIndexChange({ target: { value: String(newValue) } });
    };

    const handleAdjustPace = (seconds) => {
        const currentSec = paceToSeconds(initialPace);
        const newSec = Math.min(900, Math.max(120, currentSec + seconds));
        onInitialPaceChange({ target: { value: secondsToPace(newSec) } });
    };

    return (
        <div id="simulation-input-area" style={paceInputStyle}>
            <h2 style={{
                fontSize: '1.5rem',
                fontWeight: 800,
                color: '#4a5568',
                textAlign: 'center',
                marginBottom: '12px'
                }}>
                走力情報
            </h2>

            {/* レース開始時間〜制限時間 */}
            <div className="input-group">
                <label className="input-section-title">
                    レース開始時間〜制限時間（HH:MM）
                </label>

                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                    <input
                        type="time"
                        value={startTime}
                        onChange={onStartTimeChange}
                        className="input-small"
                    />
                    <span>〜</span>
                    <input
                        type="time"
                        value={finishTime}
                        onChange={onFinishTimeChange}
                        className="input-small"
                    />
                </div>
            </div>


            {/* ITRA */}
            <div className="input-group">
                <label htmlFor="itraIndex" className="input-section-title">
                    ITRA パフォーマンスインデックスを入力してください
                    <Tooltip text={
                        "あなたのITRAパフォーマンスインデックス(1〜1000)を入力してください。\n" +
                        "長時間走の持久力レベルの目安になります。\n\n" +
                        "▼ 参考の目安\n" +
                        "【826以上】：国際大会入賞レベル\n" +
                        "【726〜825】：国内大会入賞レベル\n" +
                        "【551〜725】：上級者\n" +
                        "【301〜550】：中級者\n" +
                        "【300以下】：初級者・登山レベル"
                    }/>
                </label>

                <input
                    type="number"
                    id="itraIndex"
                    value={itraIndex}
                    onChange={onItraIndexChange}
                    className="input-small"
                />

                <a
                    href="https://itra.run/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="itra-link"
                    style={{ 
                        marginTop: '4px', 
                        display: 'inline-block',
                        color: '#0066cc',
                        fontWeight: 600,
                        textDecoration: 'none' 
                    }}
                >
                    自分のITRA PIを調べる ↗
                </a>

                <div className="slider-control-group">
                    <button type="button" className="step-btn" onClick={() => handleAdjustItra(-1)}>－</button>
                    <input
                        type="range"
                        min="1"
                        max="1000"
                        step="1"
                        value={itraIndex}
                        onChange={onItraIndexChange}
                        className="range-slider"
                    />
                    <button type="button" className="step-btn" onClick={() => handleAdjustItra(1)}>＋</button>
                </div>
            </div>

            {/* 基準ペース */}
            <div className="input-group">
                <label className="input-section-title">
                    無理なく走れるペースを入力してください（MM:SS/km）
                    <Tooltip text={
                        "普段のジョグで自然に走れる1kmあたりのペースを入力してください。\n" +
                        "フルマラソン完走ペースが参考になります。\n" +
                        "このペースが、登り・下り・疲労によるペース補正の基準になります。\n\n" +

                        "▼ フルマラソン完走タイムからの変換式\n" +
                        "無理なく走れるペース（MM:SS/km）＝ フルマラソン完走タイム ÷ 42.195km\n\n" +
                        "▼ 代表例\n" +
                        "【5.5時間】7'49 /km\n" +
                        "【5.0時間】7'06 /km\n" +
                        "【4.5時間】6'24 /km\n" +
                        "【4.0時間】5'41 /km\n" +
                        "【3.5時間】4'59 /km\n" +
                        "【3.0時間】4'16 /km\n" +
                        "【初心者】7'00〜8'30 /km が目安"
                    }/>
                </label>

                <input
                    type="text"
                    value={initialPace}
                    onChange={onInitialPaceChange}
                    className="input-small"
                />

                <div className="slider-control-group">
                    <button type="button" className="step-btn" onClick={() => handleAdjustPace(-5)}>－</button>
                    <input
                        type="range"
                        min="120"
                        max="900"
                        step="5"
                        value={paceToSeconds(initialPace)}
                        onChange={(e) =>
                            onInitialPaceChange({
                                target: { value: secondsToPace(parseInt(e.target.value)) }
                            })
                        }
                        className="range-slider"
                    />
                    <button type="button" className="step-btn" onClick={() => handleAdjustPace(5)}>＋</button>
                </div>
            </div>

            {/* 頑張り度 */}
            <div className="input-group">
                <label className="input-section-title">
                    レース当日の頑張り度を選択してください
                    <Tooltip text={
                        "レースでどれくらい攻めるか・抑えるかを選択してください。\n" +
                        "無理なく走れるペースに対して、以下の補正を加えます。\n\n" +
                        "【S】かなり攻める\n" +
                        "【A】やや攻める\n" +
                        "【B】基準ペースのまま\n" +
                        "【C】やや抑える\n" +
                        "【D】かなり抑える\n\n" +
                        "この補正後のペースをもとに、登り・下り・疲労の計算を行います。\n" +
                        "当日の体調やレース戦略に合わせて選択してください。"
                    }/>
                </label>

                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {EFFORT_LEVELS.map(level => (
                        <button
                            key={level.key}
                            type="button"
                            className={`effort-btn ${effort === level.key ? 'active' : ''}`}
                            onClick={() => setEffort(level.key)}
                        >
                            {level.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* ★ 折りたたみ式の標準ペース */}
            <div className="input-group">
                <button
                    type="button"
                    className="collapse-toggle"
                    onClick={() => setShowBasePace(!showBasePace)}
                >
                    計算に使うペース
                    <span className="arrow">{showBasePace ? "▲" : "▼"}</span>
                </button>

                {showBasePace && (
                    <div className="base-pace-content">
                        <div className="base-pace-value">
                            {finalPace} /km
                        </div>
                        <div className="base-pace-note">
                            ※内部計算用の参考ペースです。実際の走行ペースではありません。
                        </div>
                    </div>
                )}
            </div>

            <button className="simulate-button" disabled={!isValidPace} onClick={() => onSimulate(finalPace)}>
                シミュレーション実行
            </button>
         </div>
    );
}
export default PaceInput;
